<template>
  <div id="editorial-office-submissions">
    <div class="ipub-h1 mar-b-12 line-h-40 relative">
      Manuscripts
      <ipub-radio
        v-if="
          user.codeList &&
          user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.SHOW_ME')
        "
        :checked="params.assigned"
        class="ipub-p2 line-h-40 mar-l-16"
        @change="changeAssigned"
        label="Show manuscript assigned to me only"
      />
      <ipub-radio
        v-if="
          user.codeList &&
          user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.SHOW_COUNTRY')
        "
        :checked="isChecked"
        class="ipub-p2 line-h-40 mar-l-16"
        @change="showCountry"
        label="Show country"
      />
      <div
        class="ipub-p1 right-option"
        v-if="
          user.codeList &&
          user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.EXPORT')
        "
      >
        <ipub-button type="primary" @click="exp"> Export </ipub-button>
      </div>
    </div>
    <div class="ipub-filter-div">
      <div class="ipub-filter-div-content">
        <ipub-row :gutter="[12, 16]">
          <ipub-col :span="2">
            <ipub-select
              v-model="params.journalId"
              placeholder="Select Journal"
              @change="changeJournal"
              :options="journalList"
            />
          </ipub-col>
          <ipub-col :span="4">
            <ipub-input
              placeholder="Search by article no., article title"
              @keypress.enter.native="getList()"
              v-model.trim="params.codeOrDoi"
            />
          </ipub-col>
          <ipub-col :span="6">
            <site-search-group
              v-model.trim="params.nameOrEmail"
              placeholder="Search by author’s name, email"
              @search="getList()"
              @clear="handleClear"
            />
          </ipub-col>
        </ipub-row>
        <template v-if="showAdv">
          <ipub-row :gutter="[12, 16]">
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 nowrap fw-bd">Article Status</span>
              <ipub-select
                v-model="params.status"
                @change="getList()"
                search
                @search="statusFilter"
                class="mar-l-12"
                :clear="true"
                placeholder="Select Article Status"
              >
                <template v-for="(value, key) in articleStatus">
                  <ipub-select-option
                    v-if="key !== 'UNCOMMITTED'"
                    :key="key + '123'"
                    :value="key"
                  >
                    {{ value }}
                  </ipub-select-option>
                </template>
              </ipub-select>
            </ipub-col>
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 nowrap fw-bd">Assigned Editor</span>
              <ipub-select
                v-model="params.editorId"
                @change="getList()"
                :disabled="disableAE"
                search
                @search="getEditors"
                class="mar-l-12"
                :clear="true"
                placeholder="Select Assigned Editor"
              >
                <ipub-select-option
                  v-for="item in editorList"
                  :key="'editorList' + item.id"
                  :value="item.id"
                >
                  {{ item.firstName }} {{ item.lastName }}
                </ipub-select-option>
              </ipub-select>
            </ipub-col>
          </ipub-row>
          <ipub-row :gutter="[12, 16]">
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 nowrap fw-bd">Article Type</span>
              <ipub-select
                v-model="params.typeCode"
                :disabled="disable"
                @change="getList()"
                :clear="true"
                search
                @search="getType"
                class="mar-l-12"
                placeholder="Select Article Type"
                optionKey="code"
                :options="typeList"
              />
            </ipub-col>
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 fw-bd">Country</span>
              <ipub-select
                search
                :clear="true"
                @change="getList()"
                @search="countrySearch"
                class="mar-l-12"
                placeholder="Select Country | Region"
                v-model="params.countryCode"
              >
                <ipub-select-option
                  v-for="item in countryList"
                  :value="item.code"
                  :key="'countryList' + item.code"
                >
                  {{ item.name }}
                  <template v-if="item.otherName"
                    >({{ item.otherName }})</template
                  >
                </ipub-select-option>
              </ipub-select>
            </ipub-col>
          </ipub-row>
          <ipub-row :gutter="[12, 16]">
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 nowrap fw-bd">Section</span>
              <ipub-select
                placeholder="Section"
                :disabled="disable"
                :options="sectionList"
                :tree="true"
                optionKey="id"
                :clear="true"
                @change="getList()"
                class="mar-l-12"
                v-model="params.sectionId"
              />
            </ipub-col>
            <ipub-col :span="6" class="flex">
              <span class="line-h-40 nowrap fw-bd" style="width: 100px"
                >Special Issue</span
              >
              <ipub-select
                placeholder="Select Special Issue"
                :options="specialList"
                :disabled="disable"
                search
                class="mar-l-12"
                :clear="true"
                style="width: calc(100% - 100px)"
                @search="getSpecial"
                @change="getList()"
                optionTitle="titleText"
                v-model="params.specialId"
              />
            </ipub-col>
          </ipub-row>
          <ipub-row :gutter="[12, 16]">
            <ipub-col :span="6" class="flex" style="align-items: center">
              <span class="line-h-40 nowrap fw-bd">Submission Time</span>
              <ipub-date-range-picker
                class="mar-l-12"
                style="width: 500px"
                :placeholder="['From', 'To']"
                @change="handleDateChange"
                v-model="date"
              />
            </ipub-col>
            <ipub-col :span="6" class="flex" style="align-items: center">
              <span class="line-h-40 nowrap fw-bd">Accept Time</span>
              <ipub-date-range-picker
                class="mar-l-12"
                style="width: 500px"
                :placeholder="['From', 'To']"
                @change="handleAcceptDateChange"
                v-model="acceptDate"
              />
            </ipub-col>
          </ipub-row>
          <ipub-row :gutter="[12, 16]">
            <ipub-col :span="6" class="flex" style="align-items: center">
              <span class="line-h-40 nowrap fw-bd">Status Change Time</span>
              <ipub-date-range-picker
                class="mar-l-12"
                style="width: 500px"
                :placeholder="['From', 'To']"
                @change="handleStatusDateChange"
                v-model="statusDate"
              />
            </ipub-col>
          </ipub-row>
        </template>
      </div>
      <a
        class="ipub-filter-div-button"
        @click="
          () => {
            showAdv = !showAdv
          }
        "
      >
        Advanced
        <ipub-icon
          :class="['mar-l-10 rotate', showAdv ? 'rotate-180' : '']"
          type="iconArrow"
        />
      </a>
    </div>

    <div class="ipub-list-tab">
      <a
        @click="changeStatus('')"
        :class="[
          'ipub-list-tab--status',
          params.searchStatus === '' ? 'active' : '',
        ]"
        >All ({{ countObj.all || 0 }})</a
      >
      <a
        v-for="(item, key) in STATUS"
        :key="key"
        @click="changeStatus(key)"
        :class="[
          'ipub-list-tab--status',
          params.searchStatus === key ? 'active' : '',
        ]"
        >{{ item }} ({{ countObj[key] || 0 }})</a
      >
    </div>

    <div class="page-content">
      <ipub-table
        :columns="columns"
        fixed
        @order="onOrderChanage"
        :empty="{ icon: 'iconSI_article' }"
        :emptyText="emptyText[params.searchStatus || 'all']"
        :pagination="pagination"
        :dataSource="dataSource"
      >
        <template v-slot:title="{ record }">
          <div>
            <div class="flex">
              <ipub-label size="small" cover type="warning">
                V{{ record.sign }}
              </ipub-label>
              <ipub-tooltip
                placement="bottomLeft"
                v-if="record.transferList && record.transferList.length"
                class="mar-l-10"
              >
                <div slot="title">
                  <div
                    v-for="(item, index) in record.transferList"
                    :key="record.id + 'transferList' + index"
                  >
                    Transfer from {{ item.journal.name }}
                  </div>
                </div>
                <ipub-label
                  size="small"
                  cover
                  v-if="record.transferList && record.transferList.length"
                  style="margin-left: 4px"
                >
                  Transfer
                </ipub-label>
              </ipub-tooltip>
              <span
                @click="detail(record)"
                class="ipub-p1 ipub-link mar-l-10"
                >{{ record.code }}</span
              >
              <template
                v-if="
                  user.codeList &&
                  user.codeList.includes(
                    'EDITOR_TASK.MANUSCRIPTS.MANUSCRIPT_DETAILS.MANUSCRIPT_CHECK'
                  )
                "
              >
                <div v-if="record.checked" class="mar-l-5 flex-center">
                  <ipub-icon type="iconAble" class="success-color"></ipub-icon>
                </div>
                <div v-else class="mar-l-5 flex-center">
                  <ipub-icon type="iconCancel" class="key-color"></ipub-icon>
                </div>
              </template>

              <ipub-tooltip
                placement="bottomLeft"
                class="mar-l-10 link-color ipub-c-p"
              >
                <div slot="title">Copy</div>
                <ipub-icon
                  type="iconduplicate"
                  class="flex-center"
                  @click="copyCode(record.code)"
                />
              </ipub-tooltip>
              <ipub-tooltip
                placement="bottomLeft"
                class="mar-l-10 link-color ipub-c-p"
                v-if="record.checkRemark"
              >
                <div slot="title">
                  <div
                    v-html="
                      record.checkRemark
                        .replace(/\r\n/g, '<br/>')
                        .replace(/\n/g, '<br/>')
                        .replace(/\s/g, ' ')
                    "
                  ></div>
                </div>
                <ipub-icon type="icona-CheckNote" />
              </ipub-tooltip>
            </div>
            <div class="flex">
              <ipub-tooltip placement="bottomLeft">
                <div slot="title">
                  {{ record.title }}
                </div>
                <a
                  target="_blank"
                  :href="`/editorial-office/manuscripts/details/${record.id}`"
                  class="omission-2"
                >
                  {{ record.title }}
                </a>
              </ipub-tooltip>
              <ipub-tooltip
                placement="bottomLeft"
                v-if="record.remark"
                class="mar-l-10 link-color ipub-c-p"
              >
                <div slot="title">
                  <div
                    v-html="
                      record.remark
                        .replace(/\r\n/g, '<br/>')
                        .replace(/\n/g, '<br/>')
                        .replace(/\s/g, ' ')
                    "
                  ></div>
                </div>
                <ipub-icon type="icona-ManuscriptNote" />
              </ipub-tooltip>
            </div>

            <div class="mar-b-10">
              <ipub-label
                v-if="record.ebed"
                size="small"
                style="margin-right: 4px; padding: 0 5px"
                type="link"
              >
                EB
              </ipub-label>
              <ipub-label
                v-if="record.rbed"
                size="small"
                style="margin-right: 4px; padding: 0 5px"
                type="link"
              >
                RB
              </ipub-label>
              <ipub-label
                v-if="record.geed"
                size="small"
                style="margin-right: 4px; padding: 0 5px"
                type="link"
              >
                GE
              </ipub-label>
              <ipub-label
                v-if="record.sanctioned"
                size="small"
                type="danger"
                active
                style="margin-right: 4px; padding: 0 5px"
              >
                Sanction Country
              </ipub-label>
            </div>
          </div>
        </template>
        <template v-slot:special="{ text }">
          <ipub-tooltip v-if="text" placement="bottomLeft">
            <div slot="title">
              {{ text.titleText }}
            </div>
            <div class="omission">{{ text.titleText }}</div>
          </ipub-tooltip>
          <template v-else>- -</template>
        </template>
        <template v-slot:country="{ record }">
          <div v-if="record && record.countryNameList" class="flex">
            <ipub-tooltip placement="bottomLeft">
              <div slot="title">
                <div
                  v-for="(item, index) in record.countryNameList"
                  :key="index"
                >
                  <div>{{ item }}</div>
                </div>
              </div>
              <div v-for="(item, index) in record.countryNameList" :key="index">
                <div v-show="index < 3" class="omis" style="width: 120px">
                  {{ item }}
                </div>
              </div>
            </ipub-tooltip>
          </div>
          <template v-else>- -</template>
        </template>
        <template v-slot:editor="{ text, record }">
          <div v-if="text && text.id">
            {{ text.firstName }} {{ text.lastName }}
            <ipub-tooltip placement="bottomLeft">
              <div slot="title">Change Editor</div>
            </ipub-tooltip>
          </div>
          <template v-else>- -</template>
          <a
            class="mar-l-10"
            v-if="
              user.codeList &&
              user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.ASSIGN_EDITOR')
            "
            @click="changeEditor(record)"
          >
            <ipub-icon type="iconEditPen" class="link-color" />
          </a>
        </template>
        <template v-slot:status="{ text, record }">
          <div>
            <div class="flex">
              <ipub-tooltip v-if="record.statusTime" placement="bottomLeft">
                <div slot="title">
                  {{ $moment(record.statusTime).format('DD / MM / YYYY') }}
                </div>
                <span
                  :class="
                    $moment().isAfter(record.reviseDueTime) &&
                    [
                      'PENDING_MINOR_REVISIONS',
                      'PENDING_MAJOR_REVISIONS',
                    ].includes(text)
                      ? 'key-color'
                      : ''
                  "
                  >{{ ARTICLE_STATUS[text] }}</span
                >
              </ipub-tooltip>
              <span
                v-else
                :class="
                  $moment().isAfter(record.reviseDueTime) &&
                  [
                    'PENDING_MINOR_REVISIONS',
                    'PENDING_MAJOR_REVISIONS',
                  ].includes(text)
                    ? 'key-color'
                    : ''
                "
                >{{ ARTICLE_STATUS[text] }}</span
              >
              <template
                v-if="
                  user.codeList &&
                  user.codeList.includes(
                    'EDITOR_TASK.MANUSCRIPTS.SEND_REVISED_REMIND'
                  )
                "
              >
                <template
                  v-if="
                    [
                      'PENDING_MINOR_REVISIONS',
                      'PENDING_MAJOR_REVISIONS',
                    ].includes(record.status)
                  "
                >
                  <div class="flex ipub-p4" style="align-items: center">
                    <ipub-icon
                      type="iconSend"
                      class="ipub-p4 link-color mar-l-10 ipub-cursor-pointer"
                      @click="remindAuthor(record)"
                      style="font-size: 16px"
                    />
                    <span class="ipub-p4" style="margin-left: 2px">{{
                      record.reviseRemind
                    }}</span>
                  </div>
                </template>
              </template>

              <template
                v-if="
                  user.codeList &&
                  user.codeList.includes(
                    'EDITOR_TASK.MANUSCRIPTS.SEND_ONLINE_REMIND'
                  )
                "
              >
                <div
                  class="flex ipub-p4"
                  style="align-items: center"
                  v-if="['ONLINE'].includes(record.status)"
                >
                  <ipub-icon
                    type="iconSend"
                    class="ipub-p4 link-color mar-l-10 ipub-cursor-pointer"
                    @click="
                      sendEmail(
                        record,
                        'MANUSCRIPT_ONLINE',
                        'manuscript_online'
                      )
                    "
                    style="font-size: 16px"
                  />
                  <span class="ipub-p4" style="margin-left: 2px">{{
                    record.reviseRemind
                  }}</span>
                </div>
              </template>
              <template
                v-if="
                  user.codeList &&
                  user.codeList.includes(
                    'EDITOR_TASK.MANUSCRIPTS.EDIT_REVISION_DUE_DATE'
                  )
                "
              >
                <div
                  class="flex ipub-p4"
                  style="align-items: center"
                  v-if="
                    [
                      'PENDING_MINOR_REVISIONS',
                      'PENDING_MAJOR_REVISIONS',
                    ].includes(record.status)
                  "
                >
                  <ipub-icon
                    @click="editDate(record)"
                    class="ipub-p4 link-color mar-l-10 ipub-cursor-pointer"
                    style="font-size: 16px"
                    type="iconCalendar"
                  />
                </div>
              </template>
            </div>
            <template v-if="record.reviewerStatusList">
              <template v-for="(item, key) in record.reviewerStatusList">
                <div v-if="item && item.length" :key="record.id + key">
                  <ipub-tooltip placement="rightTop">
                    <div slot="title">
                      <div
                        :class="[
                          'ipub-p3 mar-b-10',
                          'reviewer-status-list--' + key,
                        ]"
                      >
                        {{ REVIEWER_STATUS[key] }} ({{ item.length }})
                      </div>
                      <div
                        class="ipub-p4"
                        v-for="(val, num) in item"
                        :key="'item.statusReviewerList' + val.id"
                      >
                        <template
                          v-if="
                            user.codeList &&
                            user.codeList.includes(
                              'EDITOR_TASK.MANUSCRIPTS.MANUSCRIPT_DETAILS.VIEW_REPORTS'
                            )
                          "
                        >
                          <a class="ipub-link" @click="openReviewer(key, val)">
                            <div>
                              {{ num + 1 }} - {{ val.firstName }}
                              {{ val.lastName }}
                            </div>
                            <div class="mar-l-20">{{ val.email }}</div>
                          </a>
                        </template>
                        <template v-else>
                          <a
                            class="ipub-link"
                            @click="openReviewerOther(key, val)"
                          >
                            <div>
                              {{ num + 1 }} - {{ val.firstName }}
                              {{ val.lastName }}
                            </div>
                            <div class="mar-l-20">{{ val.email }}</div>
                          </a>
                        </template>
                      </div>
                    </div>
                    <div
                      :class="[
                        'reviewer-status-item ipub-p4',
                        'reviewer-status-item--' + key,
                      ]"
                    >
                      {{ REVIEWER_STATUS[key] }}({{ item.length }})
                    </div>
                  </ipub-tooltip>
                </div>
              </template>
            </template>
          </div>
        </template>
        <template v-slot:actions="{ record }">
          <ipub-tooltip
            v-show="
              user.codeList &&
              user.codeList.includes(
                'EDITOR_TASK.MANUSCRIPTS.MANUSCRIPT_DETAILS'
              )
            "
            placement="bottomLeft"
            class="border-right"
          >
            <div slot="title">Manuscript Details</div>
            <ipub-icon @click="detail(record)" type="iconViewDetails" />
          </ipub-tooltip>
          <!-- <ipub-tooltip v-show="user.codeList && user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.SET_ACADEMIC_EDITOR_VISIBILITY')"
                        placement="bottomRight"
                        class="border-right">
            <div slot="title">
              <font v-if="record.locked===true">Academic Editor can not see this manuscript details</font>
              <font v-else>Academic Editor can see this manuscript details</font>
            </div>
            <ipub-icon v-if="record.locked===true"
                       @click="lock(record)"
                       type="iconLock_Close" />
            <ipub-icon v-else
                       @click="lock(record)"
                       type="iconLock_Open" />

          </ipub-tooltip> -->

          <!-- <ipub-tooltip v-if="['SUBMITTED', 'PENDING_APC_CONFIRM'].includes(record.status)&&user.codeList&&user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.BACK_TO_AUTHOR')"
                        placement="bottomLeft"
                        class="border-right">
            <div slot="title">
              Back to Author
            </div>
            <ipub-icon @click="backToAuthor(record)"
                       type="icona-BacktoAuthor" />
          </ipub-tooltip> -->
          <!-- <ipub-tooltip v-if="['PAPER_ACCEPTED', 'PENDING_PRE_CHECK', 'PRE_CHECK_DONE', 'PENDING_MINOR_REVISIONS', 'PENDING_MAJOR_REVISIONS'].includes(record.status)&&user.codeList&&user.codeList.includes('EDITOR_TASK.MANUSCRIPTS.WITHDRAW')"
                        placement="bottomLeft"
                        class="border-right">
            <div slot="title">
              Withdraw
            </div>
            <ipub-icon @click="sendEmail(record,'WITHDRAW','widthdraw')"
                       type="iconReject" />
          </ipub-tooltip> -->
          <template
            v-if="
              user.codeList &&
              user.codeList.includes(
                'EDITOR_TASK.MANUSCRIPTS.CORRESPONDENCE_RECORD'
              )
            "
          >
            <ipub-tooltip
              placement="bottomRight"
              :class="record.status === 'PAPER_ACCEPTED' ? 'border-right' : ''"
            >
              <div slot="title">Correspondence Record</div>
              <ipub-icon
                @click="corespondenceRecord(record)"
                type="icona-CorrespondenceHistory"
              />
            </ipub-tooltip>
          </template>
          <template
            v-if="
              (user.codeList &&
                user.codeList.includes(
                  'EDITOR_TASK.MANUSCRIPTS.GENERATE_INVOICE'
                )) ||
              (user.codeList &&
                user.codeList.includes(
                  'EDITOR_TASK.MANUSCRIPTS.GENERATE_RECEIPT'
                ))
            "
          >
            <ipub-tooltip
              v-show="
                ['PAPER_ACCEPTED', 'ONLINE'].includes(record.status) &&
                !record.sanctioned
              "
              placement="bottomRight"
              action="hover"
            >
              <div
                style="font-size: 14px"
                class="hover-action-content"
                slot="title"
              >
                <div
                  class="flex ipub-c-p"
                  v-if="
                    user.codeList &&
                    user.codeList.includes(
                      'EDITOR_TASK.MANUSCRIPTS.GENERATE_INVOICE'
                    )
                  "
                  @click="createCanvans(record, 'Invoice')"
                >
                  <ipub-icon type="iconinvoice" />
                  Invoice
                </div>
                <div
                  class="flex ipub-c-p"
                  v-if="
                    user.codeList &&
                    user.codeList.includes(
                      'EDITOR_TASK.MANUSCRIPTS.GENERATE_RECEIPT'
                    )
                  "
                  @click="createCanvans(record, 'Receipt')"
                >
                  <ipub-icon type="iconreceipt" />
                  Receipt
                </div>
              </div>
              <ipub-icon type="iconGenerator" />
            </ipub-tooltip>
          </template>
        </template>
      </ipub-table>
    </div>
    <EditDate
      :isVisable="showEditDate"
      :inputData="inputData"
      @confirm="
        (e) => {
          sendEmail(e, 'MODIFIED_THE_REVISION_TIME')
        }
      "
      @cancel="cancel"
    />
    <AssignEditorDialog
      :isVisable="showAssignEditor"
      :inputData="inputData"
      @confirm="confirm"
      @cancel="cancel"
    />
    <ReviewResult
      :isVisable="showReviewResult"
      :inputData="inputData"
      @cancel="cancel"
    />
    <RejectReason
      :isVisable="showRejectReason"
      :inputData="inputData"
      @cancel="cancel"
    />
    <SendEmail
      :isVisable="showSendEmail"
      :inputData="inputData"
      :event="event"
      :files="mailFiles"
      @send="send"
      @cancel="cancel"
    />
    <CreatePDF
      :isVisable="isVisablePDF"
      :inputData="inputData"
      :type="pdfType"
      @confirm="cancel"
      @save="cancel"
      @sendEm="
        (e) => {
          sendEmail(e, 'SEND_APC_INVOICE_TO_AUTHOR', 'invoice_to_author')
        }
      "
      @cancel="cancel"
    />
  </div>
</template>

<style lang="scss" scoped>
@import './main.scss';
</style>

<script>
import main from './main'
export default main
</script>
